<template>
    <v-container>
        <v-row>
            <v-col lg="3" v-if="filter.length > 0 && !breakpoint">
                <group-filter
                    ref="filterComponent"
                    :filter="filter"
                    :stock_filter="filter_stock"
                    :mobile="false"
                    @change-filter="changeFilter($event, fireGaEvent(products?.data || []))"
                />
            </v-col>
            <v-col :lg="filter.length > 0 ? 9 : 12">
                <selected-filter
                    :filter="filter"
                    @cancel-filter="filterComponent?.resetFilter()"
                    @remove-filter="filterComponent?.removeFilter($event)"
                ></selected-filter>

                <div v-if="breakpoint" class="d-flex align-center justify-space-between my-4 ga-2">
                    <div class="flex-grow-1">
                        <v-btn :block="$vuetify.display.xs" @click="drawer = !drawer" :prepend-icon="mdiFilter" variant="tonal">{{ $t('eshop::group-page.filter') }}</v-btn>
                    </div>
                    <render-mode :mode="viewMode" @changeMode="changeMode"></render-mode>
                </div>

                <div v-if="products.data.length"
                     class="d-flex font-weight-thin justify-space-between align-center"
                     :class="{
                        'flex-row-reverse ': breakpoint
                     }"
                >
                    <div>
                        <span v-if="!$vuetify.display.xs" v-html="$t('eshop::group-page.shown:from:to:total', {
                                from: products.meta.from,
                                to: products.meta.to,
                                total: products.meta.total
                            })"></span>
                        <span v-else v-html="$tChoice('eshop::group-page.show_mobile', products.meta.total)"></span>
                    </div>
                    <div class="d-flex align-center ga-2">
                        <group-sorting
                            :sort="$page.props.sort"
                            @changeSort="changeSort"
                        />
                        <render-mode v-if="!breakpoint" :mode="viewMode" @changeMode="changeMode"></render-mode>
                    </div>
                </div>
                <div v-if="products.data.length">
                    <v-row class="mt-5">
                        <v-col v-for="(product, index) in products.data" :key="product.id"
                               :cols="cols.cols"
                               :sm="cols.sm"
                               :lg="cols.lg"
                        >
                            <v-divider v-if="index !== 0 && cols.variant === 'list'" class="mb-5"/>
                            <base-product :variant="cols.variant" :product="product"/>
                        </v-col>
                    </v-row>
                    <client-only>
                        <v-pagination
                            v-if="products.meta.last_page > 1"
                            :model-value="products.meta.current_page"
                            class="mt-3"
                            :length="products.meta.last_page"
                            :total-visible="countPagination"
                            @update:modelValue="paginate($event)"
                        >
                        </v-pagination>
                    </client-only>
                </div>
                <div v-else class="text-h3 text-red-accent-2 text-center">
                    {{$t('eshop::group-page.no_products')}}
                </div>
            </v-col>
        </v-row>
        <v-navigation-drawer
            v-if="breakpoint"
            v-model="drawer"
            :disable-resize-watcher="true"
            temporary
            width="350"
            class="filter-drawer"
            touchless
        >
            <div class="pa-4">
                <group-filter
                    ref="filterComponent"
                    :filter="filter"
                    :stock_filter="filter_stock"
                    mobile
                    @change-filter="changeFilter($event, fireGaEvent(products?.data || []))"
                    @close-filter="drawer = false"
                />
            </div>
        </v-navigation-drawer>
    </v-container>
</template>

<script>
import category from "../../layouts/category.vue";
import basicLayout from "../../../../frontend/layouts/basicLayout.vue";

export default {
    layout: [basicLayout, category]
}
</script>

<script setup>
import { computed, ref, unref, watch } from 'vue';
import BaseProduct from "../BaseProduct.vue";
import {useDisplay} from "vuetify";
import GroupFilter from "../GroupFilter.vue";
import GroupSorting from "../GroupSorting.vue";
import {usePage, router} from "@inertiajs/vue3";
import axios from "axios";
import { mdiFilter } from '@mdi/js';
import {addEvent} from "../../../../gtm";
import ClientOnly from "../../../../frontend/components/ClientOnly.vue";
import SelectedFilter from '../SelectedFilter.vue';
import { collect } from '../../../../analytics.js';
import RenderMode from '../RenderMode.vue';
import { useFilterStore } from '../../store/filter.js';
import pinia from '../../../../plugins/pinia.js';
import { changeFilter } from '../../utils/filter.js';

// eslint-disable-next-line no-undef
const props = defineProps({
    filter: Array,
    filter_stock: Object,
    products: Object,
    group: Object
})

const display = useDisplay();
const filterComponent = ref(null);
const store = useFilterStore(pinia)

const drawer = ref(false);
const viewMode = ref(usePage().props.viewMode);
const countPagination = computed(() => {
    if(display.smAndDown.value) return 3;
    if(display.mdAndDown.value) return 5;
    return 7;
})
const cols = computed(() => {
    const base = {
        variant: 'box',
        cols: 12,
        sm: 4,
        lg: 3
    };
    switch (viewMode.value) {
        case 4:
            return base;
        case 3:
            base.lg = 4;
            return base;
        case 2:
            base.lg = base.sm = 6;
            return base;
        case 1:
            return {
                cols: 12,
                sm: 12,
                lg: 12,
                variant: 'list'
            }
    }
})

const breakpoint = computed(() => {
    return ['xs', 'sm', 'md'].includes(unref(display.name));
});

const changeMode = (mode) => {
    viewMode.value = mode;
    axios.post(`/eshop/set-view-mode`, {viewMode: mode});
}

const changeSort = (sort) => {
    router.get(window.location.href, {sort: sort, page: undefined})
}

const paginate = (page) => {
    router.visit(props.products.meta.path,
        {
            only: ['products'],
            data: {page}
        },
    );
}

const fireGaEvent = (products) => {
    const gtmItems = [];
    for (let i = 0; i < products.length; i++) {
        let gtmItem = {
            item_id: products[i].id,
            item_name: products[i].name,
            price: parseFloat(products[i].price.replace(/\s/g, '').replace(/,/g, '.')).toFixed(2),
            quantity: 1,
            currency: usePage().props.currency,
        }
        if (products[i].manufacturer) {
            gtmItem['item_brand'] = products[i].manufacturer.name;
        }
        if (products[i].default_group) {
            gtmItem['item_category'] = products[i].default_group.name;
        }
        gtmItems.push(gtmItem);
    }
    addEvent({
        event: 'view_item_list', ecommerce: {
            item_list_id: props.group.id,
            item_list_name: props.group.name,
            items: [
                gtmItems
            ]
        }
    })
}

if (typeof window !== "undefined") {
    fireGaEvent(props.products.data)
}

watch(props.group, () => {
    store.filterValueCount = [];
    store.selected = [];
    store.selectedStock = null;
    store.valueSearch = [];
    collect(props.group.id, 'categories', 'detail', useDisplay().platform.value);
}, {immediate: true})

</script>

<style scoped lang="scss">
.with-hover {
    .v-card--reveal {
        align-items: center;
        bottom: 0;
        justify-content: center;
        opacity: .9;
        position: absolute;
        width: 100%;
        height: 100%;
    }
}

.filter-drawer{
  max-width: 85vw;
}

.btn-filter {
    position: fixed;
    transform: translate3d(0, -50%, 0);
    top: 50%;
    background-color: rgba(0, 0, 0, .2);
    color: white;
    left: 100%;
    border-radius: 6px;
}
:deep(.v-navigation-drawer--active){
  .btn-filter {
    transform: translate3d(0, -50%, 0);
  }
}
</style>

<style lang="scss">
.cis-slider {
    align-self: flex-end;

    input {
        display: none;
    }
}

.v-slider__container {
    position: relative;
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
}
</style>
